import React, { useState, useEffect } from 'react';
import axios from 'axios';

const OrderForm = ({ onOrderCreated }) => {
  const [formData, setFormData] = useState({
    customer_email: '',
    product_name: 'gold-1oz',
    order_type: 'buy',
    target_price: '',
    quantity: 1,
    duration_days: 7,
    phone: '',
    product_image: '',
    notes: ''
  });
  const [currentPrices, setCurrentPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCurrentPrices();
  }, []);

  const fetchCurrentPrices = async () => {
    try {
      const response = await axios.get('/api/prices/current');
      setCurrentPrices(response.data);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          product_image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/orders', {
        ...formData,
        target_price: parseFloat(formData.target_price),
        quantity: parseInt(formData.quantity),

        duration_days: parseInt(formData.duration_days)
      });

      setSuccess(`Order created successfully! Order ID: ${response.data.order_id}`);
      
      // Reset form
      setFormData({
        customer_email: '',
        product_name: 'gold-1oz',
        order_type: 'buy',
        target_price: '',
        quantity: 1,
        duration_days: 7,
        phone: '',
        product_image: '',
        notes: ''
      });

      // Notify parent component
      if (onOrderCreated) {
        setTimeout(() => onOrderCreated(), 2000);
      }

    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPrice = () => {
    return currentPrices[formData.product_name]?.price || 'Loading...';
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      }).format(price);
    }
    return price;
  };

  const getProductDisplayName = (productName) => {
    const names = {
      'gold-1g': 'Gold 1 Gram',
      'gold-10g': 'Gold 10 Grams',
      'gold-1oz': 'Gold 1 Ounce'
    };
    return names[productName] || productName;
  };

  const getPriceRecommendation = () => {
    const currentPrice = currentPrices[formData.product_name]?.price;
    if (!currentPrice || !formData.target_price) return null;

    const targetPrice = parseFloat(formData.target_price);
    const difference = ((targetPrice - currentPrice) / currentPrice * 100).toFixed(2);
    
    if (formData.order_type === 'buy') {
      if (targetPrice >= currentPrice) {
        return { type: 'warning', text: `Target price is ${difference}% above current market price` };
      } else {
        return { type: 'info', text: `Target price is ${Math.abs(difference)}% below current market price` };
      }
    } else {
      if (targetPrice <= currentPrice) {
        return { type: 'warning', text: `Target price is ${Math.abs(difference)}% below current market price` };
      } else {
        return { type: 'info', text: `Target price is ${difference}% above current market price` };
      }
    }
  };

  return (
    <div className="order-form">
      <div className="form-header">
        <h2>Create Limit Order</h2>
        <p>Set your target price and we'll execute the order when the market reaches it</p>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="customer_email">Email Address *</label>
              <input
                type="email"
                id="customer_email"
                name="customer_email"
                value={formData.customer_email}
                onChange={handleInputChange}
                required
                placeholder="your@email.com"
              />
              <small>We'll send notifications to this email</small>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+386 40 123 456"
              />
              <small>Optional contact number</small>
            </div>

            <div className="form-group">
              <label htmlFor="product_name">Product *</label>
              <select
                id="product_name"
                name="product_name"
                value={formData.product_name}
                onChange={handleInputChange}
                required
              >
                <option value="gold-1oz">Gold 1 Ounce</option>
                <option value="gold-10g">Gold 10 Grams</option>
                <option value="gold-1g">Gold 1 Gram</option>
              </select>
              <small>Current price: {formatPrice(getCurrentPrice())}</small>
            </div>

            <div className="form-group">
              <label htmlFor="quantity">Quantity *</label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                min="1"
                required
                placeholder="1"
              />
              <small>Number of items to order</small>
            </div>



            <div className="form-group">
              <label htmlFor="order_type">Order Type *</label>
              <select
                id="order_type"
                name="order_type"
                value={formData.order_type}
                onChange={handleInputChange}
                required
              >
                <option value="buy">Buy (execute when price drops to target)</option>
                <option value="sell">Sell (execute when price rises to target)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="target_price">Target Price (€) *</label>
              <input
                type="number"
                id="target_price"
                name="target_price"
                value={formData.target_price}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                required
                placeholder="0.00"
              />
              {getPriceRecommendation() && (
                <small className={`price-recommendation ${getPriceRecommendation().type}`}>
                  {getPriceRecommendation().text}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="duration_days">Duration (days) *</label>
              <select
                id="duration_days"
                name="duration_days"
                value={formData.duration_days}
                onChange={handleInputChange}
                required
              >
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>1 week</option>
                <option value={14}>2 weeks</option>
                <option value={30}>1 month</option>
                <option value={90}>3 months</option>
              </select>
              <small>Order will expire after this period if not executed</small>
            </div>

            <div className="form-group full-width">
              <label htmlFor="product_image">Product Image</label>
              <input
                type="file"
                id="product_image"
                name="product_image"
                onChange={handleFileChange}
                accept="image/*"
                className="file-input"
              />
              <small>Optional image file (JPG, PNG, GIF - max 5MB)</small>
              {formData.product_image && (
                <div className="image-preview">
                  <img 
                    src={formData.product_image} 
                    alt="Product preview" 
                    className="preview-image"
                  />
                  <button 
                    type="button" 
                    onClick={() => setFormData(prev => ({...prev, product_image: ''}))}
                    className="remove-image-btn"
                  >
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="notes">Notes (optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any additional notes about this order..."
                rows={3}
              />
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Creating Order...' : 'Create Limit Order'}
            </button>
          </div>
        </form>

        <div className="order-summary">
          <h3>Order Summary</h3>
          <div className="summary-item">
            <span>Product:</span>
            <span>{getProductDisplayName(formData.product_name)}</span>
          </div>
                      <div className="summary-item">
              <span>Quantity:</span>
              <span>{formData.quantity} piece{formData.quantity > 1 ? 's' : ''}</span>
            </div>

          <div className="summary-item">
            <span>Type:</span>
            <span className={`order-type ${formData.order_type}`}>
              {formData.order_type.toUpperCase()}
            </span>
          </div>
          <div className="summary-item">
            <span>Current Price:</span>
            <span>{formatPrice(getCurrentPrice())}</span>
          </div>
          <div className="summary-item">
            <span>Target Price:</span>
            <span>{formData.target_price ? formatPrice(parseFloat(formData.target_price)) : '-'}</span>
          </div>
          <div className="summary-item">
            <span>Duration:</span>
            <span>{formData.duration_days} days</span>
          </div>
          <div className="summary-item">
            <span>Expires:</span>
            <span>
              {new Date(Date.now() + formData.duration_days * 24 * 60 * 60 * 1000).toLocaleDateString('de-DE', {
                timeZone: 'Europe/Berlin'
              })} CET
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;